import React, { useCallback, useMemo } from 'react';
import { Platform, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import DropdownMenuLabel from '@/components/DropdownMenu/DropdownMenuLabel';
import NavigationBarButton from '@/components/Navigation/NavigationBarButton';
import Text from '@/components/Typography/Text';
import { useToolStore, useModalStore } from '@/store';
import { pickHeadlineField, truncate } from '@/components/Tools/toolDetailHelpers';

const IS_TOUCH_NATIVE = Platform.OS === 'ios' || Platform.OS === 'android';

/**
 * Header dropdown that lets the user pick a saved preset (a.k.a. example) for
 * the current tool. Selecting a preset updates the form on the tool detail
 * page via the cross-component bridge (`requestApplyExample` in the store).
 *
 * Designed to live inside the tool detail page's `headerRight`. Hidden when
 * the tool has no presets so we don't dangle an empty button.
 *
 * Native split: on iOS / Android the React Navigation header doesn't host
 * Zeego's UIMenu / PopupMenu reliably (the navigator's own touch surface
 * swallows the activation gesture and the menu silently fails to open). On
 * those platforms we open the shared `ToolPickerModal` instead — same data,
 * same callbacks, just a bottom-sheet picker UI. Web keeps the lightweight
 * Radix-backed dropdown since it works correctly there.
 */
function ToolPresetsDropdown({ slug }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { openModal } = useModalStore(state => state.actions);

  const tool = useToolStore(s => s.toolsBySlug[slug]);
  const requestApplyExample = useToolStore(s => s.requestApplyExample);

  // Only show fields that are user-visible — same filter the page uses, so the
  // preset display matches what would actually populate the form.
  const visibleFields = useMemo(() => (
    (Array.isArray(tool?.fields) ? tool.fields : []).filter(f => f?.flags?.isVisibleToUsers !== false)
  ), [tool]);

  const examples = useMemo(() => {
    const list = Array.isArray(tool?.examples) ? tool.examples : [];
    const fieldNames = new Set(visibleFields.map(f => f?.name).filter(Boolean));
    return list
      .filter(ex => ex && typeof ex === 'object' && !Array.isArray(ex))
      .map(ex => Object.fromEntries(
        Object.entries(ex).filter(([key, value]) => (
          fieldNames.has(key)
          && value !== undefined
          && value !== null
          && value !== ''
          && (!Array.isArray(value) || value.length > 0)
        ))
      ))
      .filter(ex => Object.keys(ex).length > 0);
  }, [tool, visibleFields]);

  const handleSelect = useCallback((example, index) => {
    requestApplyExample(slug, example, index);
  }, [requestApplyExample, slug]);

  // Build the per-preset row view-model once. Both the web dropdown items
  // and the native modal items consume the same shape so we don't have to
  // recompute headline / subtitle logic in two places.
  const presetWordSingular = t('pages.tools.preset') || 'Preset';
  const presetItems = useMemo(() => examples.map((example, index) => {
    const headline = pickHeadlineField(example, visibleFields);
    const presetLabel = `${presetWordSingular} ${index + 1}`;
    // Treat the headline-field as the title when it carries real prompt
    // content; if it's a file/object headline, fall back to "Preset N"
    // so we don't surface raw filenames like "portrait-original.png".
    const headlineIsString = headline && typeof headline.value === 'string';
    const title = headlineIsString
      ? truncate(headline.value, 72)
      : presetLabel;
    const subtitle = headlineIsString
      ? presetLabel
      : (headline ? truncate(headline.value, 48) : null);
    return { example, index, title, subtitle };
  }), [examples, visibleFields, presetWordSingular]);

  const presetWord = examples.length === 1
    ? (t('pages.tools.presetsCountSingle') || '%COUNT% preset')
    : (t('pages.tools.presetsCount')       || '%COUNT% presets');
  const headerKicker = presetWord.replace('%COUNT%', examples.length);

  // Native: tap → openModal. Memoised so the trigger button doesn't churn.
  const handleNativePress = useCallback(() => {
    openModal('ToolPickerModal', {
      title: t('pages.tools.tryPreset') || 'Try a preset',
      subtitle: headerKicker,
      items: presetItems.map(({ example, index, title, subtitle }) => ({
        key: `preset-${index}`,
        icon: 'lightbulb',
        solidIcon: true,
        title,
        subtitle,
        onSelect: () => handleSelect(example, index),
      })),
      emptyText: t('pages.tools.fillFormHint') || 'Pre-fills the form below.',
    });
  }, [openModal, t, headerKicker, presetItems, handleSelect]);

  // No presets → don't render the trigger at all to keep the header clean.
  if (!examples.length) return null;

  // ---------- Native (iOS / Android) ----------
  // Plain NavigationBarButton with a real onPress that fires our modal.
  // No Zeego wrapping — that's what was silently swallowing the tap inside
  // the React Navigation header.
  if (IS_TOUCH_NATIVE) {
    return (
      <NavigationBarButton
        icon='lightbulb'
        tooltip={t('pages.tools.headerPresetsTooltip') || 'Presets'}
        accessibilityLabel={t('pages.tools.headerPresetsTooltip') || 'Presets'}
        onPress={handleNativePress}
      />
    );
  }

  // ---------- Web / macOS ----------
  // Radix-backed dropdown stays — it renders correctly inside react-navigation
  // on those platforms and avoids the modal-switch animation cost.
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <NavigationBarButton
          icon='lightbulb'
          tooltip={t('pages.tools.headerPresetsTooltip') || 'Presets'}
          accessibilityLabel={t('pages.tools.headerPresetsTooltip') || 'Presets'}
        />
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        className='DropdownMenuContent'
        align='end'
        sideOffset={6}
        style={{ minWidth: 280 }}
      >
        <DropdownMenuLabel
          text={`${t('pages.tools.tryPreset') || 'Try a preset'} · ${headerKicker}`}
        />
        {presetItems.map(({ example, index, title, subtitle }) => (
          <DropdownMenuItem
            key={`preset-${index}`}
            title={title}
            subtitle={subtitle}
            onSelect={() => handleSelect(example, index)}
          />
        ))}
        <View style={{ paddingHorizontal: 14, paddingVertical: 10, borderTopWidth: 1, borderTopColor: colors.border_color_secondary }}>
          <Text size='xs' color='tertiary'>
            {t('pages.tools.usePreset') || 'Use this preset'} · {t('pages.tools.fillFormHint')?.split('.')[0]
              || 'Pre-fills the form below'}
          </Text>
        </View>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

export default React.memo(ToolPresetsDropdown);
